import React, { useState, useEffect, useCallback } from "react";
import TodoCard from "./TodoCard";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const PRIORITY_COLORS = {
  Low: "rgba(10,10,15,0.58)", Medium: "#0033FF", High: "#f97316", Urgent: "#ef4444",
};

export default function StoreDashboardPage({ store, storeName, onNavigate }) {
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [kanbanRequests, setKanbanRequests] = useState([]);
  const [tickets, setTickets] = useState([]);

  const currentUser = (() => {
    try { return JSON.parse(sessionStorage.getItem("nycmfg-user")); } catch { return null; }
  })();

  const fetchData = useCallback(() => {
    fetch(`${API_URL}/api/products?store=${store}`).then((r) => r.json()).then((d) => { if (Array.isArray(d)) setProducts(d); }).catch(() => {});
    fetch(`${API_URL}/api/restock?store=${store}`).then((r) => r.json()).then((d) => { if (Array.isArray(d)) setRequests(d); }).catch(() => {});
    fetch(`${API_URL}/api/support-tickets?store=${store}`).then((r) => r.json()).then((d) => { if (Array.isArray(d)) setTickets(d); }).catch(() => {});
    try {
      const all = JSON.parse(localStorage.getItem("store-requests-v1") || "{}");
      setKanbanRequests(all[store] || []);
    } catch { setKanbanRequests([]); }
  }, [store]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const inventoryKanban = kanbanRequests.filter((r) => r.type === "inventory" && r.status !== "Finished");
  const hardwareKanban = kanbanRequests.filter((r) => r.type === "hardware" && r.status !== "Finished");

  // A bundle holds no stock of its own — what it can ship is worked out from
  // what is in it. Counting it in Total Stock would count those units twice.
  const stockOf = (p) => (p.isBundle ? p.derivedQuantity || 0 : p.quantity || 0);
  const totalProducts = products.length;
  const totalStock = products.reduce((s, p) => s + (p.isBundle ? 0 : p.quantity || 0), 0);
  const outOfStock = products.filter((p) => stockOf(p) === 0).length;
  const lowStock = products.filter((p) => stockOf(p) > 0 && stockOf(p) <= (p.lowStockThreshold ?? 10)).length;
  const pendingRequests = requests.filter((r) => r.status === "pending");

  return (
    <div>
      {/* Greeting */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 26, fontWeight: 700, color: "#0A0A0F", marginBottom: 4 }}>
          Hello{currentUser ? `, ${currentUser.name}` : ""}!
        </h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{storeName} Dashboard</p>
      </div>

      {/* Field App Link */}
      <a href="/field" target="_blank" rel="noopener noreferrer" style={{
        display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 0,
        border: "2px solid #0A0A0F", background: "#FFFFFF", color: "#0033FF",
        fontSize: 14, fontWeight: 600, textDecoration: "none", marginBottom: 24, transition: "border-color .15s",
      }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#0033FF")}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#0A0A0F")}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 7V5a2 2 0 012-2h2" /><path d="M17 3h2a2 2 0 012 2v2" /><path d="M21 17v2a2 2 0 01-2 2h-2" /><path d="M7 21H5a2 2 0 01-2-2v-2" />
          <rect x="7" y="7" width="10" height="10" rx="1" />
        </svg>
        Open Field App
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
        </svg>
      </a>

      {/* To do — above the stats on purpose: a job of work under five numbers
          stops being a job of work. */}
      <TodoCard store={store} storeName={storeName} onNavigate={onNavigate} />

      {/* Overview Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 28 }}>
        <div style={S.statCard}><span style={S.statLabel}>Total Products</span><span style={S.statValue}>{totalProducts}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Total Stock</span><span style={S.statValue}>{totalStock}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Out of Stock</span><span style={{ ...S.statValue, color: outOfStock > 0 ? "#ef4444" : "#34d399" }}>{outOfStock}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Low Stock</span><span style={{ ...S.statValue, color: lowStock > 0 ? "#f59e0b" : "#34d399" }}>{lowStock}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Pending Requests</span><span style={{ ...S.statValue, color: pendingRequests.length > 0 ? "#0033FF" : "#34d399" }}>{pendingRequests.length}</span></div>
      </div>

      {/* Kanban Request Summaries */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* Inventory Requests Kanban */}
        <div
          onClick={() => onNavigate && onNavigate("store-requests")}
          style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden", cursor: "pointer", transition: "border-color .15s" }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#0033FF")}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#0A0A0F")}
        >
          <div style={{ padding: "12px 20px", background: "#FFFFFF", borderBottom: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)" }}>
              Inventory Requests
              {inventoryKanban.length > 0 && <span style={{ ...S.jobCountBadge, marginLeft: 8 }}>{inventoryKanban.length}</span>}
            </span>
            <span style={{ fontSize: 12, color: "#0033FF" }}>View All &rarr;</span>
          </div>
          <div style={{ maxHeight: 250, overflowY: "auto" }}>
            {inventoryKanban.length > 0 ? inventoryKanban.slice(0, 5).map((r) => {
              const pc = PRIORITY_COLORS[r.priority] || "rgba(10,10,15,0.58)";
              return (
                <div key={r.id} style={{ padding: "10px 20px", borderBottom: "1px solid rgba(10,10,15,0.12)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "#0A0A0F", display: "flex", alignItems: "center", gap: 6 }}>
                      {r.name}
                      {r.priority && r.priority !== "Low" && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 0, background: `${pc}20`, color: pc, border: `1px solid ${pc}30` }}>{r.priority}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>{r.requestedBy || ""} · {r.date || ""}</div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 0, background: r.status === "Requested" ? "rgba(200,149,108,0.1)" : "rgba(59,130,246,0.1)", color: r.status === "Requested" ? "#0033FF" : "#3b82f6" }}>{r.status}</span>
                </div>
              );
            }) : (
              <div style={{ padding: 30, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>No active requests</div>
            )}
          </div>
        </div>

        {/* Hardware/Supplies Requests Kanban */}
        <div
          onClick={() => onNavigate && onNavigate("store-requests")}
          style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden", cursor: "pointer", transition: "border-color .15s" }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#0033FF")}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#0A0A0F")}
        >
          <div style={{ padding: "12px 20px", background: "#FFFFFF", borderBottom: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)" }}>
              Hardware & Supplies Requests
              {hardwareKanban.length > 0 && <span style={{ ...S.jobCountBadge, marginLeft: 8 }}>{hardwareKanban.length}</span>}
            </span>
            <span style={{ fontSize: 12, color: "#0033FF" }}>View All &rarr;</span>
          </div>
          <div style={{ maxHeight: 250, overflowY: "auto" }}>
            {hardwareKanban.length > 0 ? hardwareKanban.slice(0, 5).map((r) => {
              const pc = PRIORITY_COLORS[r.priority] || "rgba(10,10,15,0.58)";
              return (
                <div key={r.id} style={{ padding: "10px 20px", borderBottom: "1px solid rgba(10,10,15,0.12)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "#0A0A0F", display: "flex", alignItems: "center", gap: 6 }}>
                      {r.name}
                      {r.priority && r.priority !== "Low" && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 0, background: `${pc}20`, color: pc, border: `1px solid ${pc}30` }}>{r.priority}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>{r.requestedBy || ""} · {r.date || ""}</div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 0, background: r.status === "Requested" ? "rgba(200,149,108,0.1)" : "rgba(59,130,246,0.1)", color: r.status === "Requested" ? "#0033FF" : "#3b82f6" }}>{r.status}</span>
                </div>
              );
            }) : (
              <div style={{ padding: 30, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>No active requests</div>
            )}
          </div>
        </div>
      </div>

      {/* Support Tickets */}
      {(() => {
        const isAdmin = currentUser?.role === "admin";
        const myName = currentUser?.name || "";
        const openTickets = tickets.filter((t) => t.status !== "Resolved" && (isAdmin || (t.assignedTo || t.assigned_to || "") === myName)).sort((a, b) => {
          const pOrder = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
          return (pOrder[a.priority] ?? 4) - (pOrder[b.priority] ?? 4);
        });
        return (
          <div
            onClick={() => onNavigate && onNavigate("store-support")}
            style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden", cursor: "pointer", transition: "border-color .15s" }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#0033FF")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#0A0A0F")}
          >
            <div style={{ padding: "12px 20px", background: "#FFFFFF", borderBottom: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)" }}>
                {isAdmin ? "Support Tickets" : "My Tickets"}
                {openTickets.length > 0 && <span style={{ ...S.jobCountBadge, marginLeft: 8 }}>{openTickets.length}</span>}
              </span>
              <span style={{ fontSize: 12, color: "#0033FF" }}>View All &rarr;</span>
            </div>
            <div style={{ ...S.tableWrap, border: "none", borderRadius: 0 }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, width: "30%" }}>Customer</th>
                    <th style={{ ...S.th, width: "20%" }}>Category</th>
                    <th style={{ ...S.th, width: "15%" }}>Platform</th>
                    <th style={{ ...S.th, width: "15%" }}>Priority</th>
                    <th style={{ ...S.th, width: "20%" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {openTickets.slice(0, 8).map((t) => {
                    const pc = PRIORITY_COLORS[t.priority] || "rgba(10,10,15,0.58)";
                    const statusColor = t.status === "New Ticket" ? "#0033FF" : "#3b82f6";
                    return (
                      <tr key={t.id}>
                        <td style={{ ...S.td, fontWeight: 500, color: "#0A0A0F" }}>{t.customerName || t.customer_name || "—"}</td>
                        <td style={{ ...S.td, fontSize: 13, color: "#0A0A0F" }}>{t.category || "—"}</td>
                        <td style={{ ...S.td, fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{t.platform || "—"}</td>
                        <td style={S.td}>
                          <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 0, fontSize: 12, fontWeight: 600, background: `${pc}20`, color: pc, border: `1px solid ${pc}30` }}>
                            {t.priority || "Medium"}
                          </span>
                        </td>
                        <td style={S.td}>
                          <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 0, background: `${statusColor}15`, color: statusColor }}>
                            {t.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {openTickets.length === 0 && (
                    <tr><td colSpan={5} style={{ ...S.td, textAlign: "center", color: "#34d399", padding: 30 }}>{isAdmin ? "No open tickets" : "No tickets assigned to you"}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
